import { fromZonedTime } from 'date-fns-tz';
import { BookmakerName, PregameGameStatus, Sport } from '@/constants/main';
import { timeUntilEventKickoff } from '@/helpers/event';
import { AbstractModel } from '@/models/abstractModel';
import { IEventItemBaseDataAPI } from '@/types/event/base';
import { IExtraCornersGoalLine, IExtraCornersHandicapLine, IExtraGoalLine, IExtraHandicapLine, IExtraSpreadLine } from '@/types/event/pregame';

export abstract class AbstractEvent extends AbstractModel {
    public bookmakerId: number;

    public btsPriceNo: string | null;
    public btsPriceYes: string | null;
    public cornersAwayFt: number | null;
    public cornersAwayHt: number | null;
    public cornersAwayIr: number | null;
    public cornersAwayIrHt: number | null;
    public cornersGoalHandicap: string | null;
    public cornersGoalLine: number | null;
    public cornersGoalPriceOver: string | null;
    public cornersGoalPriceUnder: string | null;
    public cornersHdpHandicap: string | null;
    public cornersHdpJollywhere: 0 | 1 | 2 | null;
    public cornersHdpLine: number | null;
    public cornersHdpPriceAway: string | null;
    public cornersHdpPriceHome: string | null;
    public cornersHomeFt: number | null;
    public cornersHomeHt: number | null;
    public cornersHomeIr: number | null;
    public cornersHomeIrHt: number | null;
    public countryIso2: string | null;
    public countryIso3: string | null;
    public countryName: string | null;
    public createdAt: string;
    public customEventId: string;
    public customEventIdLiveWrong: string | null;
    public customLeagueAgeCategoryId: number;
    public customLeagueCategoryId: number;
    public customLeagueCountryId: number | null;
    public customLeagueId: number;
    public customLeagueIsUseTradeball: 0 | 1;
    public customLeagueIsUseWl: 0 | 1;
    public customLeagueLimitsCategoryId: number;
    public customLeagueMarketCategoryId: number;
    public customLeagueName: string;
    public customTeamAwayId: number;
    public customTeamAwayName: string;
    public customTeamHomeId: number;
    public customTeamHomeName: string;
    public dateOpen: string;
    public dateToday: string | null;
    public dcPrice12: string | null;
    public dcPrice1x: string | null;
    public dcPriceX2: string | null;
    public dnbOpenPriceAway: string | null;
    public dnbOpenPriceHome: string | null;
    public dnbPriceAway: string | null;
    public dnbPriceHome: string | null;
    public eventDate: string;
    public extra: any | null;
    public extraCornersGoalLines: IExtraCornersGoalLine[] = [];
    public extraCornersHandicapLines: IExtraCornersHandicapLine[] = [];
    public extraGoalAwayLines: IExtraGoalLine[] = [];
    public extraGoalHomeLines: IExtraGoalLine[] = [];
    public extraGoalLines: IExtraGoalLine[] = [];
    public extraHandicapLines: IExtraHandicapLine[] = [];
    public extraSpreadLines: IExtraSpreadLine[] = [];
    public feedCustomEventExcludeId: number | null;
    public feedEventExcludeId: number | null;
    public ftTimeAt: string | null;
    public goalAwayHandicap: string | null;
    public goalAwayLine: number | null;
    public goalAwayPriceOver: string | null;
    public goalAwayPriceUnder: string | null;
    public goalHandicap: string;
    public goalHomeHandicap: string | null;
    public goalHomeLine: number | null;
    public goalHomePriceOver: string | null;
    public goalHomePriceUnder: string | null;
    public goalLine: number;
    public goalPriceOver: string;
    public goalPriceUnder: string;
    public hash: string;
    public hdpHandicap: string;
    public hdpJollywhere: 0 | 1 | 2 | null;
    public hdpLine: number;
    public hdpPriceAway: string;
    public hdpPriceHome: string;
    public id: number;
    public internalId: string;
    public is1stHalfStarted: 0 | 1;
    public is2ndHalfStarted: 0 | 1;
    public isCustomEventExcludedFromFeeds: boolean;
    public isDballExist: 0 | 1;
    public isEarlyExist: 0 | 1;
    public isEventBettingExcluded: boolean;
    public isExcludedFromFeeds: boolean;
    public isHtAlready: 0 | 1;
    public isInverted: 0 | 1;
    public isLiveStarted: 0 | 1;
    public isNvenue: 0 | 1;
    public isScoreManually: 0 | 1;
    public leagueId: number;
    public leagueIdOrigin: number;
    public leagueName: string;
    public liveInternalId: string | null;
    public mlPriceAway: string | null;
    public mlPriceHome: string | null;
    public modifiedAt: string;
    public oneXTwoPriceAway: string;
    public oneXTwoPriceDraw: string;
    public oneXTwoPriceHome: string;
    public scoreFt: string | null;
    public scoreFtChangesCount: number;
    public scoreFtProblem: string | null;
    public scoreFtSum: number | null;
    public scoreHt: string | null;
    public scoreHtChangesCount: number;
    public scoreHtProblem: string | null;
    public scoreHtSum: number | null;
    public scoreIr: string | null;
    public scoreIrHt: string | null;
    public sourceId: number;
    public sportId: number;
    public spreadHandicap: string | null;
    public spreadJollywhere: 0 | 1 | 2 | null;
    public spreadLine: number | null;
    public spreadPriceAway: string | null;
    public spreadPriceHome: string | null;
    public teamAwayId: number;
    public teamAwayIdOrigin: number;
    public teamAwayName: string;
    public teamHomeId: number;
    public teamHomeIdOrigin: number;
    public teamHomeName: string;
    constructor(data: IEventItemBaseDataAPI) {
        super();
        this.id = data.id;
        this.createdAt = data.created_at;
        this.modifiedAt = data.modified_at;
        this.bookmakerId = data.bookmaker_id;
        this.sportId = data.sport_id ?? Sport.Football;
        this.internalId = data.internal_id;
        this.liveInternalId = data.live_internal_id;
        this.customEventId = data.custom_event_id;
        this.eventDate = data.date;
        this.dateOpen = data.date_open;
        this.dateToday = data.date_today;
        this.leagueId = data.league_id;
        this.leagueIdOrigin = data.league_id_origin;
        this.teamHomeId = data.team_home_id;
        this.teamHomeIdOrigin = data.team_home_id_origin;
        this.teamAwayId = data.team_away_id;
        this.teamAwayIdOrigin = data.team_away_id_origin;
        this.scoreFtProblem = data.score_ft_problem;
        this.scoreHtProblem = data.score_ht_problem;
        this.scoreFtChangesCount = data.score_ft_changes_count;
        this.scoreHtChangesCount = data.score_ht_changes_count;
        this.scoreFt = data.score_ft;
        this.scoreHt = data.score_ht;
        this.scoreIr = data.score_ir;
        this.scoreIrHt = data.score_ir_ht;
        this.scoreHtSum = data.score_ht_sum;
        this.scoreFtSum = data.score_ft_sum;
        this.isScoreManually = data.is_score_manually;
        this.cornersHomeIr = data.corners_home_ir;
        this.cornersAwayIr = data.corners_away_ir;
        this.cornersHomeIrHt = data.corners_home_ir_ht;
        this.cornersAwayIrHt = data.corners_away_ir_ht;
        this.cornersHomeFt = data.corners_home_ft;
        this.cornersAwayFt = data.corners_away_ft;
        this.cornersHomeHt = data.corners_home_ht;
        this.cornersAwayHt = data.corners_away_ht;
        this.isEarlyExist = data.is_early_exist;
        this.isDballExist = data.is_dball_exist;
        this.isNvenue = data.is_nvenue;
        this.isHtAlready = data.is_ht_already;
        this.ftTimeAt = data.ft_time_at;
        this.is1stHalfStarted = data.is_1st_half_started;
        this.is2ndHalfStarted = data.is_2nd_half_started;
        this.isLiveStarted = data.is_live_started;
        this.isInverted = data.is_inverted;
        this.sourceId = data.source_id;
        this.hash = data.hash;
        this.extra = data.extra;
        this.customEventIdLiveWrong = data.custom_event_id_live_wrong;
        this.teamHomeName = data.team_home_name;
        this.teamAwayName = data.team_away_name;
        this.customTeamHomeName = data.custom_team_home_name;
        this.customTeamAwayName = data.custom_team_away_name;
        this.customLeagueName = data.custom_league_name;
        this.customLeagueId = data.custom_league_id;
        this.customLeagueAgeCategoryId = data.custom_league_age_category_id;
        this.customLeagueCategoryId = data.custom_league_category_id;
        this.customLeagueCountryId = data.custom_league_country_id;
        this.customLeagueIsUseTradeball = data.custom_league_is_use_tradeball;
        this.customLeagueIsUseWl = data.custom_league_is_use_wl;
        this.customLeagueLimitsCategoryId = data.custom_league_limits_category_id;
        this.customLeagueMarketCategoryId = data.custom_league_market_category_id;
        this.customTeamAwayId = data.custom_team_away_id;
        this.customTeamHomeId = data.custom_team_home_id;
        this.countryName = data.country_name;
        this.countryIso2 = data.country_iso2;
        this.countryIso3 = data.country_iso3;
        this.oneXTwoPriceAway = data['1x2_price_away'] ?? '';
        this.oneXTwoPriceDraw = data['1x2_price_draw'] ?? '';
        this.oneXTwoPriceHome = data['1x2_price_home'] ?? '';
        this.btsPriceNo = data.bts_price_no ?? null;
        this.btsPriceYes = data.bts_price_yes ?? null;
        this.goalHandicap = data.goal_handicap ?? '';
        this.goalLine = data.goal_line ?? 0;
        this.goalPriceOver = data.goal_price_over ?? '';
        this.goalPriceUnder = data.goal_price_under ?? '';
        this.goalHomeHandicap = data.goal_home_handicap ?? null;
        this.goalHomeLine = data.goal_home_line ?? null;
        this.goalHomePriceOver = data.goal_home_price_over ?? null;
        this.goalHomePriceUnder = data.goal_home_price_under ?? null;
        this.goalAwayHandicap = data.goal_away_handicap ?? null;
        this.goalAwayLine = data.goal_away_line ?? null;
        this.goalAwayPriceOver = data.goal_away_price_over ?? null;
        this.goalAwayPriceUnder = data.goal_away_price_under ?? null;
        this.hdpHandicap = data.hdp_handicap ?? '';
        this.hdpJollywhere = data.hdp_jollywhere ?? null;
        this.hdpLine = data.hdp_line ?? 0;
        this.hdpPriceAway = data.hdp_price_away ?? '';
        this.hdpPriceHome = data.hdp_price_home ?? '';
        this.mlPriceHome = data.ml_price_home ?? null;
        this.mlPriceAway = data.ml_price_away ?? null;
        this.spreadLine = data.spread_line ?? null;
        this.spreadJollywhere = data.spread_jollywhere ?? null;
        this.spreadHandicap = data.spread_handicap ?? null;
        this.spreadPriceHome = data.spread_price_home ?? null;
        this.spreadPriceAway = data.spread_price_away ?? null;
        this.leagueName = data.league_name;
        this.feedEventExcludeId = data.feed_event_exclude_id;
        this.isExcludedFromFeeds = data.feed_event_exclude_id !== null;
        this.feedCustomEventExcludeId = data.feed_custom_event_exclude_id ?? null;
        this.isCustomEventExcludedFromFeeds = (data.feed_custom_event_exclude_id ?? null) !== null;
        this.isEventBettingExcluded = (data.is_event_betting_excluded ?? 0) === 1;
        this.dcPrice1x = data.dc_price_1x ?? null;
        this.dcPrice12 = data.dc_price_12 ?? null;
        this.dcPriceX2 = data.dc_price_x2 ?? null;
        this.cornersGoalHandicap = data.corners_goal_handicap ?? null;
        this.cornersGoalLine = data.corners_goal_line ?? null;
        this.cornersGoalPriceOver = data.corners_goal_price_over ?? null;
        this.cornersGoalPriceUnder = data.corners_goal_price_under ?? null;
        this.cornersHdpLine = data.corners_hdp_line ?? null;
        this.cornersHdpJollywhere = data.corners_hdp_jollywhere ?? null;
        this.cornersHdpHandicap = data.corners_hdp_handicap ?? null;
        this.cornersHdpPriceHome = data.corners_hdp_price_home ?? null;
        this.cornersHdpPriceAway = data.corners_hdp_price_away ?? null;
        this.dnbPriceHome = data.dnb_price_home ?? null;
        this.dnbPriceAway = data.dnb_price_away ?? null;
        this.dnbOpenPriceHome = null;
        this.dnbOpenPriceAway = null;
        this.extraGoalLines = AbstractEvent.parseJsonArray<IExtraGoalLine>(data.extra_goal_lines);
        this.extraGoalHomeLines = AbstractEvent.parseJsonArray<IExtraGoalLine>(data.extra_goal_home_lines);
        this.extraGoalAwayLines = AbstractEvent.parseJsonArray<IExtraGoalLine>(data.extra_goal_away_lines);
        this.extraHandicapLines = AbstractEvent.parseJsonArray<IExtraHandicapLine>(data.extra_handicap_lines);
        this.extraSpreadLines = AbstractEvent.parseJsonArray<IExtraSpreadLine>(data.extra_spread_lines);
        this.extraCornersGoalLines = AbstractEvent.parseJsonArray<IExtraCornersGoalLine>(data.extra_corners_goal_lines);
        this.extraCornersHandicapLines = AbstractEvent.parseJsonArray<IExtraCornersHandicapLine>(data.extra_corners_handicap_lines);
    }

    private static parseJsonArray<T>(jsonString: string | null | undefined): T[] {
        try {
            return JSON.parse(jsonString ?? '[]');
        } catch (error) {
            return [];
        }
    }

    get currentScore(): string {
        return this.scoreIr || this.scoreFt || '';
    }

    get gameStatus(): PregameGameStatus {
        if (this.scoreFt) {
            return PregameGameStatus.Finished;
        } else if (this.isLive) {
            return PregameGameStatus.Live;
        } else if (fromZonedTime(new Date(this.eventDate), 'Europe/London').getTime() - new Date().getTime() > 0) {
            return PregameGameStatus.Upcoming;
        } else {
            return PregameGameStatus.Undefined;
        }
    }

    get hasStatusWarning(): boolean {
        return this.isInverted === 1 || this.isExcludedFromFeeds || this.isCustomEventExcludedFromFeeds || this.isEventBettingExcluded;
    }

    get infoTooltip(): string {
        const warnings: string[] = [];

        if (this.isInverted === 1) {
            warnings.push(this.t('pregame.isInverted'));
        }

        if (this.isExcludedFromFeeds) {
            const bookmakerName = BookmakerName[this.bookmakerId] || 'Bookmaker';
            warnings.push(this.t('pregame.eventExcludedFromFeeds', { bookmaker: bookmakerName }));
        }

        if (this.isCustomEventExcludedFromFeeds) {
            const bookmakerName = BookmakerName[this.bookmakerId] || 'Bookmaker';
            warnings.push(this.t('pregame.eventExcludedFromFeedsWithOthers', { bookmaker: bookmakerName }));
        }

        if (this.isEventBettingExcluded) {
            warnings.push(this.t('pregame.eventExcludedOnThirdPartyWebsite'));
        }

        if (warnings.length > 0) {
            return warnings.join('<br>');
        }

        return '';
    }

    get isLive(): boolean {
        return this.isLiveStarted === 1;
    }

    get teamAwayColor(): string {
        const jollywhere = this.sportId === Sport.Basketball ? this.spreadJollywhere : this.hdpJollywhere;
        if (jollywhere === 2) return 'text-tbRed';
        return 'text-blue';
    }

    get teamHomeColor(): string {
        const jollywhere = this.sportId === Sport.Basketball ? this.spreadJollywhere : this.hdpJollywhere;
        if (jollywhere === 1) return 'text-tbRed';
        return 'text-blue';
    }

    public getTimeToEventStart() {
        const leftMinutes = Math.floor((fromZonedTime(new Date(this.eventDate), 'Europe/London').getTime() - new Date().getTime()) / 60000);
        return timeUntilEventKickoff(leftMinutes);
    }
}
